import { useEffect, useState, useCallback, useRef } from 'react'
import websocketService, { NotificationMessage } from '../services/websocketService'

interface UseRealTimeNotificationsOptions {
  autoConnect?: boolean
  notificationTypes?: string[]
  onNotification?: (notification: NotificationMessage) => void
  onError?: (error: Error) => void
}

export const useRealTimeNotifications = (options: UseRealTimeNotificationsOptions = {}) => {
  const {
    autoConnect = true,
    notificationTypes = [],
    onNotification,
    onError,
  } = options

  const [isConnected, setIsConnected] = useState(false)
  const [notifications, setNotifications] = useState<NotificationMessage[]>([])
  const [unreadCount, setUnreadCount] = useState(0)
  const listenersRef = useRef<(() => void)[]>([])

  // 连接WebSocket
  const connect = useCallback(() => {
    try {
      websocketService.connect()
    } catch (error) {
      console.error('连接WebSocket失败:', error)
      onError?.(error as Error)
    }
  }, [onError])

  // 断开WebSocket连接
  const disconnect = useCallback(() => {
    websocketService.disconnect()
  }, [])

  // 发送消息
  const sendMessage = useCallback((message: any) => {
    websocketService.send(message)
  }, [])

  // 请求通知权限
  const requestPermission = useCallback(async () => {
    return await websocketService.requestNotificationPermission()
  }, [])

  // 清除所有通知
  const clearNotifications = useCallback(() => {
    setNotifications([])
    setUnreadCount(0)
  }, [])

  // 标记通知为已读
  const markAsRead = useCallback((notificationId: string) => {
    setNotifications(prev =>
      prev.map(notification =>
        notification.id === notificationId
          ? { ...notification, read: true }
          : notification
      )
    )

    setUnreadCount(prev => Math.max(0, prev - 1))
  }, [])

  // 标记所有通知为已读
  const markAllAsRead = useCallback(() => {
    setNotifications(prev =>
      prev.map(notification => ({ ...notification, read: true }))
    )
    setUnreadCount(0)
  }, [])

  // 移除通知
  const removeNotification = useCallback((notificationId: string) => {
    setNotifications(prev => {
      const filtered = prev.filter(notification => notification.id !== notificationId)
      const removed = prev.find(notification => notification.id === notificationId)
      if (removed && !removed.read) {
        setUnreadCount(count => Math.max(0, count - 1))
      }
      return filtered
    })
  }, [])

  // 处理新通知
  const handleNotification = useCallback((notification: NotificationMessage) => {
    setNotifications(prev => [notification, ...prev])
    setUnreadCount(prev => prev + 1)

    // 调用外部回调
    onNotification?.(notification)

    // 如果指定了通知类型过滤
    if (notificationTypes.length > 0 && !notificationTypes.includes(notification.type)) {
      return
    }
  }, [onNotification, notificationTypes])

  // 处理连接状态变化
  const handleConnectionChange = useCallback(() => {
    setIsConnected(websocketService.isConnected())
  }, [])

  // 处理错误
  const handleError = useCallback((error: Error) => {
    console.error('WebSocket错误:', error)
    onError?.(error)
  }, [onError])

  // 初始化连接
  useEffect(() => {
    if (autoConnect) {
      connect()
    }

    // 监听连接状态
    const connectionListener = () => handleConnectionChange()
    listenersRef.current.push(
      websocketService.addListener('connection', connectionListener)
    )

    // 监听所有通知
    const notificationListener = (notification: NotificationMessage) => {
      handleNotification(notification)
    }
    listenersRef.current.push(
      websocketService.addListener('*', notificationListener)
    )

    // 监听连接错误
    const errorListener = (error: any) => {
      handleError(new Error(error.message || 'WebSocket连接错误'))
    }
    listenersRef.current.push(
      websocketService.addListener('error', errorListener)
    )

    return () => {
      // 清理监听器
      listenersRef.current.forEach(unsubscribe => unsubscribe())
      listenersRef.current = []

      // 如果是自动连接，则断开连接
      if (autoConnect) {
        disconnect()
      }
    }
  }, [autoConnect, connect, disconnect, handleConnectionChange, handleNotification, handleError])

  // 监听页面可见性变化
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // 页面变为可见时检查连接状态
        setIsConnected(websocketService.isConnected())

        // 如果未连接且是自动连接模式，尝试重连
        if (!websocketService.isConnected() && autoConnect) {
          connect()
        }
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [autoConnect, connect])

  // 手动刷新连接状态
  useEffect(() => {
    const interval = setInterval(() => {
      setIsConnected(websocketService.isConnected())
    }, 5000)

    return () => clearInterval(interval)
  }, [])

  return {
    // 状态
    isConnected,
    notifications,
    unreadCount,

    // 方法
    connect,
    disconnect,
    sendMessage,
    requestPermission,
    clearNotifications,
    markAsRead,
    markAllAsRead,
    removeNotification,
  }
}